import { useState, forwardRef, useImperativeHandle, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BaseModal } from '../ui/BaseModal';
import { DeleteNodePreview } from './DeleteNodePreview';
import { useDeleteNode, useGetNodeDeletionPreview } from '../../services/documents.service';

interface DeleteDocumentModalProps {
  companyId: string;
}

export interface DeleteDocumentModalRef {
  open: (nodeId: string, nodeName: string) => void;
}

export const DeleteDocumentModal = forwardRef<DeleteDocumentModalRef, DeleteDocumentModalProps>(
  ({ companyId }, ref) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [nodeId, setNodeId] = useState<string | null>(null);
    const [nodeName, setNodeName] = useState<string>('');
    const [isConfirmed, setIsConfirmed] = useState(false);

    const deleteNodeMutation = useDeleteNode(companyId);
    const { data: preview, isLoading: isPreviewLoading } = useGetNodeDeletionPreview(
      companyId,
      isOpen ? nodeId : null,
    );

    useImperativeHandle(ref, () => ({
      open: (id: string, name: string) => {
        setNodeId(id);
        setNodeName(name);
        setIsConfirmed(false);
        setIsOpen(true);
      },
    }));

    useEffect(() => {
      if (!isOpen) {
        setIsConfirmed(false);
        setNodeId(null);
        setNodeName('');
      }
    }, [isOpen]);

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      if (!nodeId || !isConfirmed) return;

      deleteNodeMutation.mutate(nodeId, { onSuccess: handleClose });
    };

    const handleClose = () => {
      setIsOpen(false);
    };

    const isFolder = preview?.isFolder ?? false;
    const isSubmitting = deleteNodeMutation.isPending;
    const canSubmit = isConfirmed && !isSubmitting && !isPreviewLoading;

    const suffix = isFolder ? '_folder' : '';

    const footer = (
      <div className="flex gap-4">
        <button
          type="button"
          onClick={handleClose}
          className="flex-1 px-4 py-2 bg-gray-100 rounded-md text-gray-900 font-medium hover:bg-gray-200 transition"
          disabled={isSubmitting}
        >
          {t('documents.delete_document_modal.cancel')}
        </button>
        <button
          type="submit"
          form="delete-document-form"
          className="flex-1 px-4 py-2 bg-primary text-white rounded-md font-medium transition disabled:opacity-50"
          disabled={!canSubmit}
        >
          {t(`documents.delete_document_modal.submit${suffix}`)}
        </button>
      </div>
    );

    return (
      <BaseModal
        isOpen={isOpen}
        onClose={handleClose}
        title={t(`documents.delete_document_modal.title${suffix}`)}
        footer={footer}
        maxWidth="md"
      >
        <form id="delete-document-form" onSubmit={handleSubmit} className="flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            <p className="text-base font-medium text-gray-900">
              {t(`documents.delete_document_modal.message${suffix}`)}
            </p>
            <div className="border border-gray-200 rounded-md px-3 py-3">
              <span className="text-sm font-medium text-gray-900">{nodeName}</span>
            </div>
          </div>

          <DeleteNodePreview preview={preview} isLoading={isPreviewLoading} />

          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={isConfirmed}
              onChange={(e) => setIsConfirmed(e.target.checked)}
              className="w-5 h-5 mt-0.5 rounded border-gray-300 text-primary focus:ring-primary"
            />
            <span className="text-sm font-medium text-gray-900">
              {t(`documents.delete_document_modal.confirm_checkbox${suffix}`)}
            </span>
          </label>
        </form>
      </BaseModal>
    );
  },
);

DeleteDocumentModal.displayName = 'DeleteDocumentModal';
